import { HttpClient, HttpHeaders } from "@angular/common/http";
import { computed, effect, inject, Injectable, resource } from "@angular/core";
import { firstValueFrom, Subject, combineLatest, filter, take } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { IStatusObjectModel, IStepsModel } from "@app/views-interfaces";
import { AppSettingsService } from "@shared/app-settings-service";
import { DashboardsService } from "@shared/dashboards-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { IStepsResponse } from "./steps-response.interface";

interface StepsResource {
    dashboardId: string;
}

@Injectable({ providedIn: "root" })
export class StepsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly http = inject(HttpClient);

    private stepsLoadedEmitter = new Subject();
    public stepsLoadedEvent$ = this.stepsLoadedEmitter.asObservable();
    private isLoading = true;
    private forceReload = false;
    private stepsCache: Map<string, Promise<IStepsModel[] | undefined>> = new Map();
    private stepsResource = resource<IStepsModel[] | undefined, StepsResource>({
        params: () => ({
            dashboardId: this.dashboardsService.currentId(),
        }),
        loader: async ({ params }) => {
            const { dashboardId } = params;
            try {
                if (!dashboardId) return;

                const cachedSteps = this.stepsCache.get(dashboardId);
                if (cachedSteps && !this.forceReload) return cachedSteps;

                const stepsPromise = this.loadSteps(dashboardId).then((steps) => {
                    if (steps === undefined) this.stepsCache.delete(dashboardId);
                    return steps;
                });
                this.stepsCache.set(dashboardId, stepsPromise);
                this.forceReload = false;
                return stepsPromise;
            } finally {
                this.isLoading = false;
            }
        },
    });
    private stepsResourceStatus$ = toObservable(this.stepsResource.status);
    public currentSteps = computed(() => this.stepsResource.value());
    private currentSteps$ = toObservable(this.currentSteps);
    public currentStepsById = computed(() => new Map(this.currentSteps()?.map((step) => [step.Id, step])));
    public currentStepByTaxReturnStatusId = computed(() => {
        const stepsByStatusId = new Map<string, IStepsModel>();

        for (const step of this.currentSteps() ?? []) {
            for (const status of step.Statuses) {
                stepsByStatusId.set(status.TaxReturnStatusId, step);
            }
        }

        return stepsByStatusId;
    });
    public currentStatusByTaxReturnStatusId = computed(() => {
        const statusesById = new Map<string, IStatusObjectModel>();

        for (const step of this.currentSteps() ?? []) {
            for (const status of step.Statuses) {
                statusesById.set(status.TaxReturnStatusId, status);
            }
        }

        return statusesById;
    });

    static readonly statesIndex = {
        notStarted: 0,
        active: 1,
        completed: 2,
        all: 3,
    };

    constructor() {
        this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            this.isLoading = true;
        });

        effect(() => {
            const steps = this.currentSteps();
            if (steps === undefined) return;

            this.isLoading = false;
            this.stepsLoadedEmitter.next(steps);
        });
    }

    public refresh(forceReload = false): void {
        this.isLoading = true;
        this.forceReload = forceReload;
        this.stepsResource.reload();
    }

    private async loadSteps(dashboardId: string): Promise<IStepsModel[] | undefined> {
        try {
            const result = await firstValueFrom(
                this.http.get<IStepsResponse>(this.getUrl("steps/", dashboardId), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code === 200) return result.Steps;
        } catch (error) {
            console.error(error);
        }

        this.responseErrorHandlerService.showPopup({
            typeLayout: "ERROR",
            messageCode: "defaultError",
        });

        return;
    }

    public async getStepsList(): Promise<IStepsModel[]> {
        await this.waitForSteps();
        return this.currentSteps() ?? [];
    }

    public addStep(stepData: IStepsModel) {
        return this.http.post<{ Code: number; Step: IStepsModel }>(this.getUrl(), stepData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    public updateStep(stepData: IStepsModel) {
        return this.http.put<{ Code: number; Steps: string[] }>(this.getUrl(), stepData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    public deleteStep(stepId: string) {
        return this.http.delete<{ Code: number; Step: string }>(this.getUrl() + stepId);
    }

    private async waitForSteps(): Promise<void> {
        await firstValueFrom(
            combineLatest([this.stepsResourceStatus$, this.currentSteps$]).pipe(
                filter(
                    ([status, steps]) => !this.isLoading && status !== "loading" && status !== "reloading" && steps !== undefined && steps !== null,
                ),
                take(1),
            ),
        );
    }

    private getUrl(path = "steps/", dashboardId = this.dashboardsService.currentId()) {
        return (
            this.appSettingsService.getSetting("StepsService_URL") +
            this.appSettingsService.getSetting("StepsService_Version") +
            path +
            dashboardId +
            "/"
        );
    }
}
